import {useTranslation} from "react-i18next";
import {useCallback, useEffect, useRef, useState} from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CancelIcon from "@mui/icons-material/Cancel";
import {TabPanel} from "../components/tab-panel-comp";
import {
    Alert, Box, Button,
    Card,
    CardContent,
    Chip, CircularProgress,
    Dialog, DialogActions,
    DialogContent,
    DialogTitle,
    Grid, IconButton, Paper, Tooltip,
    Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Tabs,
    Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PdfIcon from "@mui/icons-material/PictureAsPdf";
import ExcelIcon from "@mui/icons-material/TableChart";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import InventoryIcon from "@mui/icons-material/Inventory";
import StorefrontIcon from "@mui/icons-material/Storefront";
import RepeatIcon from "@mui/icons-material/Repeat";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {formatDateNow} from "../i18n/date-formatting";
import axiosInstance from "../config/axios-config";
import {useUser} from "../providers/user-context";
import OrderApprovalPermission from "../components/orders/order-approval-permission";
import OrderConfirmationEntry from "../components/orders/order-confirmation-entry";
import OrderAmounts from "../components/orders/order-amounts";
import PharmacyOrderSupplierDetails from "../components/orders/pharmacy-order-supplier-details";
import {formatOrderMoney, singleOrderCurrency} from "../components/orders/order-money";
import {exportToPdf, exportToExcel} from "../utils/export-utils";
import {buildShippingAddress} from "../data-tables-distributor/orders/order-api";

const emptyDetails = [];

export default function OrderDetailsDialog({open, orderNumber, selectedDistributor, onClose, onReorder, onOpenRevision}) {
    const {t, i18n} = useTranslation()
    const {tenantId, userIdentity} = useUser();
    const [activeTab, setActiveTab] = useState(0)
    const [details, setDetails] = useState({}), [refreshVersion, setRefreshVersion] = useState(0);
    const [tenantDetails, setTenantDetails] = useState({}), [tenantRefreshVersion, setTenantRefreshVersion] = useState(0);
    const [exporting, setExporting] = useState(false), [exportError, setExportError] = useState(false);
    const exportRequest = useRef(null);
    const resolvedViews = useRef({});
    const scope = [open, orderNumber, tenantId, userIdentity, i18n.language].join(":");
    useEffect(() => {setExporting(false); setExportError(false); return () => exportRequest.current?.abort();}, [scope]);
    const current = details.scope === scope ? details : {};
    const loading = open && !current.commonDetails;
    const commonDetails = current.commonDetails || emptyDetails, orderDistributors = current.orderDistributors || emptyDetails;
    const tenant = tenantDetails.scope === scope ? tenantDetails : {};
    const currentTenant = tenant.data || null;
    const activeSupplier = orderDistributors[activeTab - 1];
    const exportSupplier = async (supplier, format) => {
        exportRequest.current?.abort(); const controller = new AbortController(); exportRequest.current = controller;
        setExporting(true); setExportError(false);
        try {
            const recipient = {...supplier, orderNumber, pharmacyName: currentTenant?.tenantName || currentTenant?.tenantNameEn,
                pharmacyEmail: currentTenant?.tenantEmail};
            await (format === "pdf" ? exportToPdf : exportToExcel)(recipient, buildShippingAddress({pharmacy: currentTenant}), supplier.orderProducts, i18n.language, controller.signal);
        } catch {if (!controller.signal.aborted) {setExportError(true);}} finally {if (!controller.signal.aborted) {setExporting(false);}}
    };
    const refreshDetails = useCallback(view => {
        // A terminal read can follow a parent remount; fence repeated notifications to prevent a reload loop.
        if (view?.proposalId) {
            if (resolvedViews.current.scope !== scope) resolvedViews.current = {scope, keys: new Set()};
            const key = JSON.stringify([view.proposalId, view.state, Object.entries(view.current?.versions || {}).sort()]);
            if (resolvedViews.current.keys.has(key)) return;
            resolvedViews.current.keys.add(key);
        }
        setRefreshVersion(version => version + 1);
    }, [scope]);

    // A confirmation changes effective quantities and the timeline; reload the complete view under the same identity fence.
    useEffect(() => {
        if (!open || !orderNumber || !tenantId) return;
        const controller = new AbortController();
        const config = {signal: controller.signal, timeout: 10000, headers: {"Language-Context": i18n.language.toUpperCase() || "EN"}};
        // Keep the mounted supplier panel and its confirmation observer during a background refresh.
        setDetails(previous => ({...(previous.scope === scope ? previous : {}), scope, loading: true, error: false}));
        Promise.all([
            axiosInstance.get("/api/v1/orders/pharmacy/common-details", {...config, params: {orderNumber}}),
            axiosInstance.get(`/api/v1/orders/pharmacy/order-products/${encodeURIComponent(orderNumber)}`, config),
        ]).then(([common, suppliers]) => {
            if (!controller.signal.aborted) setDetails({scope, commonDetails: common.data, orderDistributors: suppliers.data});
        }).catch(error => {
            if (!controller.signal.aborted) {
                console.error("Error fetching order details:", error);
                setDetails(previous => ({...(previous.scope === scope ? previous : {}), scope, loading: false, error: true}));
            }
        });
        return () => controller.abort();
    }, [scope, open, orderNumber, tenantId, i18n.language, refreshVersion]);

    // Profile metadata is optional: its failure or delay must not hide an otherwise readable order.
    useEffect(() => {
        if (!open || !orderNumber || !tenantId) return;
        const controller = new AbortController();
        setTenantDetails({scope, loading: true});
        axiosInstance.get(`/api/v1/tenants/${tenantId}`, {signal: controller.signal, timeout: 10000,
            headers: {"Language-Context": i18n.language.toUpperCase() || "EN"}})
            .then(({data}) => {if (!controller.signal.aborted) setTenantDetails({scope, data});})
            .catch(() => {if (!controller.signal.aborted) setTenantDetails({scope, error: true});});
        return () => controller.abort();
    }, [scope, open, orderNumber, tenantId, i18n.language, tenantRefreshVersion]);

    // Move useEffect to the top, before any conditional returns
    useEffect(() => {
        const index = selectedDistributor ? orderDistributors.findIndex((d) => d.distributorId === selectedDistributor.distributorId) : -1;
        setActiveTab(index + 1) // Tab 0 is the overview if no matching supplier remains.
    }, [selectedDistributor, orderDistributors, orderNumber])


    // Format date
    const formatDate = (dateString) => {
        return formatDateNow(dateString, i18n.language, true)
    }

    // Get status icon and color based on order status
    const getStatusInfo = (status) => {
        switch (status.toUpperCase()) {
            case "PENDING":
                return {icon: <PendingIcon/>, color: "default"}
            case "COMPLETED":
                return {icon: <CheckCircleIcon/>, color: "success"}
            case "PROCESSING":
                return {icon: <PendingIcon/>, color: "warning"}
            case "SHIPPED":
                return {icon: <LocalShippingIcon/>, color: "info"}
            case "CANCELLED":
                return {icon: <CancelIcon/>, color: "error"}
            default:
                return {icon: <PendingIcon/>, color: "default"}
        }
    }


    return (
        <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth PaperProps={{sx: {
            m: {xs: 1, sm: 2}, width: {xs: "calc(100% - 16px)", sm: "calc(100% - 32px)"},
            maxHeight: {xs: "calc(100% - 16px)", sm: "calc(100% - 32px)"}
        }}}>
            <DialogTitle sx={{display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2}}>
                <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                    <InventoryIcon color="primary"/>
                    <Typography variant="h6" component="span">{t("OrderDetails")} - {orderNumber}</Typography>
                </Box>
                <Box sx={{display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap"}}>
                    {activeSupplier && ["pdf", "excel"].map(format => <Button key={format} size="small" variant="outlined"
                        startIcon={format === "pdf" ? <PdfIcon/> : <ExcelIcon/>}
                        disabled={exporting || current.loading || current.error || !currentTenant}
                        onClick={() => exportSupplier(activeSupplier, format)}>
                        {exporting ? t("Exporting") : `${t("ExportAs")} ${format === "pdf" ? "PDF" : "Excel"}`}
                    </Button>)}
                    <Tooltip title={t("Close")}><IconButton size="small" aria-label={t("Close")} onClick={onClose}><CloseIcon/></IconButton></Tooltip>
                </Box>
            </DialogTitle>
            <DialogContent dividers sx={{p: 3, "& .MuiCard-root": {borderRadius: 2}}}>
                {exportError && <Alert severity="error">{t("OrderQueueExportFailed")}</Alert>}
                {tenant.error && <Alert severity="warning" sx={{mb: 2}} action={<Button onClick={() => setTenantRefreshVersion(version => version + 1)}>{t("Retry")}</Button>}>
                    {t("PharmacyTenantDetailsLoadFailed")}</Alert>}
                {current.error && <Alert severity="error" action={<Button onClick={() => refreshDetails()}>{t("Retry")}</Button>}>
                    {t("PharmacyOrderDetailsLoadFailed")}</Alert>}
                {current.loading && !loading && <Typography role="status">{t("Loading")}</Typography>}
                {loading ? !current.error && <Box display="flex" justifyContent="center" alignItems="center" height={300} role="status">
                    <CircularProgress/>
                </Box> : <>
                <Tabs
                    value={activeTab}
                    onChange={(e, newValue) => setActiveTab(newValue)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{borderBottom: 1, borderColor: "divider", mb: 2}}
                >
                    <Tab label={t("Overview")}/>
                    {orderDistributors.map((distributorOrder, index) => {
                        const statusInfo = getStatusInfo(distributorOrder.status)
                        return (
                            <Tab
                                key={index}
                                label={
                                    <Box sx={{display: "flex", alignItems: "center"}}>
                                        {distributorOrder.distributorName}
                                        <Chip
                                            icon={statusInfo.icon}
                                            label={t(distributorOrder.status)}
                                            color={statusInfo.color}
                                            size="small"
                                            sx={{ml: 1}}
                                        />
                                    </Box>
                                }
                            />
                        )
                    })}
                </Tabs>

                {/* Overview Tab */}
                <TabPanel value={activeTab} index={0} typePanel="orders">
                    <Grid container spacing={3}>
                        {/* Order Summary */}
                        <Grid item xs={12}>
                            <Card variant="outlined">
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        {t("OrderSummary")}
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Box sx={{display: "flex", alignItems: "center", mb: 1}}>
                                                <CalendarTodayIcon fontSize="small"
                                                                   sx={{mr: 1, color: "text.secondary"}}/>
                                                <Typography variant="body2" color="text.secondary">
                                                    {t("OrderDate")}
                                                </Typography>
                                            </Box>
                                            <Typography
                                                variant="body1">{formatDate(commonDetails[0]?.orderDate)}</Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Box sx={{display: "flex", alignItems: "center", mb: 1}}>
                                                <AttachMoneyIcon fontSize="small"
                                                                 sx={{mr: 1, color: "text.secondary"}}/>
                                                <Typography variant="body2" color="text.secondary">
                                                    {t("TotalAmount")}
                                                </Typography>
                                            </Box>
                                            <Typography variant="body1" fontWeight="bold">
                                                {t("OrderAmountsSeeBreakdown")}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Box sx={{display: "flex", alignItems: "center", mb: 1}}>
                                                <InventoryIcon fontSize="small" sx={{mr: 1, color: "text.secondary"}}/>
                                                <Typography variant="body2" color="text.secondary">
                                                    {t("TotalItems")}
                                                </Typography>
                                            </Box>
                                            <Typography
                                                variant="body1">{commonDetails[0]?.productsTotalCount}</Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Box sx={{display: "flex", alignItems: "center", mb: 1}}>
                                                <StorefrontIcon fontSize="small" sx={{mr: 1, color: "text.secondary"}}/>
                                                <Typography variant="body2" color="text.secondary">
                                                    {t("Distributors")}
                                                </Typography>
                                            </Box>
                                            <Typography variant="body1">{orderDistributors.length}</Typography>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Distributor Status Summary */}
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>
                                {t("DistributorStatus")}
                            </Typography>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t("Distributor")}</TableCell>
                                            <TableCell>{t("Status")}</TableCell>
                                            <TableCell align="right">{t("Items")}</TableCell>
                                            <TableCell align="right">{t("Total")}</TableCell>
                                            <TableCell align="right">{t("Actions")}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {orderDistributors.map((distributorOrder, index) => {
                                            const statusInfo = getStatusInfo(distributorOrder.status)
                                            return (
                                                <TableRow key={index}>
                                                    <TableCell>{distributorOrder.distributorName}</TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            icon={statusInfo.icon}
                                                            label={t(distributorOrder.status)}
                                                            color={statusInfo.color}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {distributorOrder.productCount}
                                                    </TableCell>
                                                    <TableCell
                                                        align="right">{formatOrderMoney(distributorOrder.totalPrice, singleOrderCurrency(distributorOrder.orderProducts), i18n.language)}</TableCell>
                                                    <TableCell align="right">
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            startIcon={<RepeatIcon/>}
                                                            onClick={() => {
                                                                onReorder(distributorOrder)
                                                                onClose()
                                                            }}
                                                        >
                                                            {t("Reorder")}
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>

                        {/*Shipping and Payment Info*/}
                        <Grid item xs={12} md={6}>
                            <Card variant="outlined">
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        {t("ShippingInformation")}
                                    </Typography>
                                    {tenant.loading && <Typography variant="body2" role="status">{t("Loading")}</Typography>}
                                    <Typography variant="subtitle2" gutterBottom>
                                        {t("ShippingAddress")}:
                                    </Typography>
                                    <Typography variant="body2">
                                        {currentTenant?.tenantNameEn}
                                        <br/><br/>
                                        {currentTenant?.address?.street}
                                        <br/>
                                        {currentTenant?.address?.city}, {currentTenant?.address?.country}
                                        <br/><br/>
                                        {currentTenant?.phones?.map(phone => `${phone.phoneNumberCode} ${phone.phoneNumber}`).join(", ")}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Card variant="outlined">
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        {t("PaymentInformation")}
                                    </Typography>
                                    <Typography variant="body2" paragraph>
                                        <strong>{t("PaymentMethod")}:</strong> Bank Transfer
                                    </Typography>
                                    <Typography variant="body2" paragraph>
                                        <strong>{t("PaymentStatus")}:</strong> PAID
                                    </Typography>

                                    <Typography variant="body2" paragraph>
                                        <strong>{t("PaymentDate")}:</strong> -
                                    </Typography>

                                    <Typography variant="body2" paragraph>
                                        <strong>{t("TransactionID")}:</strong> -
                                    </Typography>


                                    <Box sx={{mt: 2}}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            {t("OrderTotal")}:
                                        </Typography>
                                        <Table size="small">
                                            <TableBody>
                                                <TableRow>
                                                    <TableCell>{t("Subtotal")}</TableCell>
                                                    <TableCell align="right">{t("OrderAmountsSeeBreakdown")}</TableCell>
                                                </TableRow>
                                                {/*{order.discount > 0 && (*/}
                                                {/*    <TableRow>*/}
                                                {/*        <TableCell>{t("Discount")}</TableCell>*/}
                                                {/*        <TableCell*/}
                                                {/*            align="right">-{order.discount.toFixed(2)}֏</TableCell>*/}
                                                {/*    </TableRow>*/}
                                                {/*)}*/}
                                                {commonDetails[0]?.shippingCost >= 0 && (
                                                    <TableRow>
                                                        <TableCell>{t("ShippingCost")}</TableCell>
                                                        <TableCell
                                                            align="right">{t("OrderAmountsSeeBreakdown")}</TableCell>
                                                    </TableRow>
                                                )}
                                                <TableRow>
                                                    <TableCell sx={{fontWeight: "bold"}}>{t("Total")}</TableCell>
                                                    <TableCell align="right" sx={{fontWeight: "bold"}}>
                                                        {t("OrderAmountsSeeBreakdown")}
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* Distributor Tabs */}
                {orderDistributors.map((distributorOrder, index) => (
                    <TabPanel key={index} value={activeTab} index={index + 1}>
                        <OrderAmounts lineId={distributorOrder.orderId} active={open && activeTab === index + 1} revision={refreshVersion}/>
                        <OrderApprovalPermission lineId={distributorOrder.orderId} active={open && activeTab === index + 1}/>
                        <OrderConfirmationEntry key={distributorOrder.orderId} lineId={distributorOrder.orderId}
                            orderStatus={distributorOrder.status} active={open && activeTab === index + 1} onRefresh={refreshDetails}/>
                        <PharmacyOrderSupplierDetails order={distributorOrder} statusInfo={getStatusInfo(distributorOrder.status)}
                            onOpenRevision={onOpenRevision} onReorder={() => {onReorder(distributorOrder); onClose();}}/>
                    </TabPanel>
                ))}
                </>}
            </DialogContent>
            <DialogActions sx={{p: 3, bgcolor: "grey.50", flexWrap: "wrap", gap: 1}}>
                <Button onClick={onClose} color="primary">
                    {t("Close")}
                </Button>
                <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<HelpOutlineIcon/>}
                    onClick={() => {
                        // Handle support request
                        onClose()
                    }}
                >
                    {t("ContactSupport")}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
